import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';
import { loadGoogleCharts } from './googleChartsLoader';

const AdminDashboard = () => {
  const [userRoles, setUserRoles] = useState({ admin: 0, supervisor: 0, user: 0 });
  const [meetingStatus, setMeetingStatus] = useState({ completed: 0, pending: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const userChartRef = useRef(null);
  const meetingChartRef = useRef(null);

  // Fetch data for the dashboard
  useEffect(() => {
    setIsLoading(true);
    fetch(`${ApiURl}/get_reports.php`)
      .then((response) => response.json())
      .then((data) => {
        setUserRoles(data.user_roles || { admin: 0, supervisor: 0, user: 0 });
        setMeetingStatus(data.meeting_status || { completed: 0, pending: 0 });
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      });
  }, []);

  // Generate Pie Chart Data
  const generateUserRoleData = () => {
    const total = userRoles.admin + userRoles.supervisor + userRoles.user;
    if (total === 0) {
      return [
        ['Role', 'Count'],
        ['Superadmin', 1],
        ['Supervisor', 1],
        ['User', 1],
      ];
    }
    return [
      ['Role', 'Count'],
      ['Superadmin', userRoles.admin],
      ['Supervisor', userRoles.supervisor],
      ['User', userRoles.user],
    ];
  };

  const generateMeetingStatusData = () => {
    const total = meetingStatus.completed + meetingStatus.pending;
    if (total === 0) {
      return [
        ['Status', 'Count'],
        ['Completed', 1],
        ['Pending', 1],
      ];
    }
    return [
      ['Status', 'Count'],
      ['Completed', meetingStatus.completed],
      ['Pending', meetingStatus.pending],
    ];
  };

  // Draw Google Charts
  useEffect(() => {
    loadGoogleCharts().then(() => {
      const drawCharts = () => {
        // User Roles Pie Chart
        const userDataTable = window.google.visualization.arrayToDataTable(
          generateUserRoleData()
        );
        const userOptions = {
          title: 'User Roles Distribution',
          is3D: true,
          colors: ['#14B8A6', '#F59E0B', '#6B7280'],
          chartArea: { width: '85%', height: '75%' },
          legend: {
            position: 'bottom',
            textStyle: { color: '#1F2937', fontName: 'Inter', fontSize: 14 },
          },
          titleTextStyle: {
            color: '#1F2937',
            fontName: 'Inter',
            fontSize: 20,
            bold: true,
          },
          backgroundColor: '#F9FAFB',
          pieSliceText: 'percentage',
          pieSliceTextStyle: { color: '#FFFFFF', fontName: 'Inter', fontSize: 14 },
          slices: {
            0: { offset: 0.1 },
            1: { offset: 0.05 },
            2: { offset: 0.05 },
          },
          animation: {
            startup: true,
            duration: 1000,
            easing: 'out',
          },
        };
        const userChart = new window.google.visualization.PieChart(userChartRef.current);
        userChart.draw(userDataTable, userOptions);

        // Meeting Status Pie Chart
        const meetingDataTable = window.google.visualization.arrayToDataTable(
          generateMeetingStatusData()
        );
        const meetingOptions = {
          title: 'Meeting Status Distribution',
          is3D: true,
          colors: ['#14B8A6', '#F59E0B'],
          chartArea: { width: '85%', height: '75%' },
          legend: {
            position: 'bottom',
            textStyle: { color: '#1F2937', fontName: 'Inter', fontSize: 14 },
          },
          titleTextStyle: {
            color: '#1F2937',
            fontName: 'Inter',
            fontSize: 20,
            bold: true,
          },
          backgroundColor: '#F9FAFB',
          pieSliceText: 'percentage',
          pieSliceTextStyle: { color: '#FFFFFF', fontName: 'Inter', fontSize: 14 },
          slices: {
            0: { offset: 0.1 },
            1: { offset: 0.05 },
          },
          animation: {
            startup: true,
            duration: 1000,
            easing: 'out',
          },
        };
        const meetingChart = new window.google.visualization.PieChart(
          meetingChartRef.current
        );
        meetingChart.draw(meetingDataTable, meetingOptions);
      };

      if (userRoles.admin || userRoles.supervisor || userRoles.user || meetingStatus.completed || meetingStatus.pending) {
        drawCharts();
      }
    });
  }, [userRoles, meetingStatus]);

  // Handle Logout
  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('full_name');
    navigate('/');
  };

  // Toggle Sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Menu Items
  const menuItems = [
    { name: 'Dashboard', path: '/scan', icon: 'M4 6h16M4 12h16M4 18h7' },
    { name: 'Reports', path: '/superReports', icon: 'M9 17v1m0 0H6a2 2 0 01-2-2V7a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2h-3m-6 0v1' },
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-[Inter]">
      {/* Embedded CSS for animations and responsiveness */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .chart-container {
          min-height: 16rem; /* 256px */
          min-width: 100%;
        }
        @media (min-width: 640px) {
          .chart-container {
            min-height: 20rem; /* 320px */
          }
        }
        @media (min-width: 1024px) {
          .chart-container {
            min-height: 24rem; /* 384px */
          }
        }
        .sidebar {
          transition: transform 0.3s ease-in-out;
        }
        @media (min-width: 1024px) {
          .sidebar {
            position: fixed;
            transform: translateX(0) !important;
          }
        }
      `}</style>

      {/* Mobile Sidebar Toggle Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition-all duration-200 hover:shadow-lg"
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? 'Close Sidebar' : 'Open Sidebar'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={isSidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
          />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`fixed lg:static w-64 bg-gradient-to-b from-teal-600 to-cyan-600 text-white shadow-2xl transform transition-transform duration-300 ease-in-out sidebar ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 z-40 h-full lg:h-screen`}
      >
        <div className="p-6 border-b border-teal-700">
          <h2 className="text-xl font-bold flex items-center animate-slide-in">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Superadmin Dashboard
          </h2>
        </div>
        <ul className="mt-4 space-y-2 p-4">
          {menuItems.map((item, index) => (
            <li key={index}>
              <button
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
                className="flex items-center w-full text-left p-3 rounded-lg hover:bg-teal-700 hover:text-white transition-all duration-200 hover:shadow-md animate-slide-in"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Overlay for Mobile Sidebar */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto lg:ml-64">
        {/* Navbar with Logout */}
        <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg p-4 flex justify-between items-center fixed w-full lg:w-[calc(100%-16rem)] z-20">
          <h1 className="text-xl sm:text-2xl font-bold">Superadmin Panel</h1>
          <button
            onClick={handleLogout}
            className="flex items-center bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </nav>

        {/* Dashboard Content */}
        <div className="p-6 sm:p-8 lg:p-10 bg-gray-50 min-h-[calc(100vh-4rem)] pt-20 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 mb-6 sm:mb-8 lg:mb-10">
            Dashboard Overview
          </h1>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <svg
                className="animate-spin h-10 w-10 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : (
            /* Charts Section */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10">
              {/* User Roles Pie Chart */}
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 transition-transform duration-300 hover:shadow-2xl hover:-translate-y-1">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-6">
                  User Roles Distribution
                </h3>
                <div ref={userChartRef} className="chart-container"></div>
              </div>
              {/* Meeting Status Pie Chart */}
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 transition-transform duration-300 hover:shadow-2xl hover:-translate-y-1">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-6">
                  Meeting Status Distribution
                </h3>
                <div ref={meetingChartRef} className="chart-container"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;